import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { http, mediaUrl } from "../lib/api";

const PlayerStateCtx = createContext(null);
const PlayerActionsCtx = createContext(null);
export const usePlayerActions = () => useContext(PlayerActionsCtx);
export const usePlayer = () => ({ ...useContext(PlayerStateCtx), ...useContext(PlayerActionsCtx) });

const useAudioState = (audio) => {
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  useEffect(() => {
    const a = audio.current;
    const on = {
      play: () => setPlaying(true),
      pause: () => setPlaying(false),
      timeupdate: () => setTime(a.currentTime),
      loadedmetadata: () => setDuration(a.duration || 0),
    };
    Object.entries(on).forEach(([k, f]) => a.addEventListener(k, f));
    return () => Object.entries(on).forEach(([k, f]) => a.removeEventListener(k, f));
  }, [audio]);
  return { playing, time, setTime, duration };
};

const useStoredFlag = (key) => {
  const [value, setValue] = useState(localStorage.getItem(key) !== "0");
  const set = useCallback((v) => { localStorage.setItem(key, v ? "1" : "0"); setValue(v); }, [key]);
  return [value, set];
};

const useAudioEngine = () => {
  const audio = useRef(new Audio());
  const counted = useRef(new Set());
  const [current, setCurrent] = useState(null);
  const load = useCallback((song, at = 0) => {
    const a = audio.current;
    a.src = mediaUrl(song.audio_path);
    if (at > 0) a.addEventListener("loadedmetadata", () => { a.currentTime = Math.min(at, (a.duration || at) - 0.5); }, { once: true });
    a.play().catch(() => {});
    setCurrent(song);
    if (!counted.current.has(song.id) && !song.id.endsWith("-src")) {
      counted.current.add(song.id);
      http.post(`/songs/${song.id}/play`).catch(() => {});
    }
  }, []);
  return { audio, current, setCurrent, load };
};

const playable = (list) => list.filter((s) => s.audio_path);

const useEndedHandler = (audio, { repeat, autoNext, queue, current, load, setTime }) => {
  useEffect(() => {
    const a = audio.current;
    const ended = () => {
      if (repeat) { a.currentTime = 0; a.play(); return; }
      const i = queue.findIndex((s) => s.id === current?.id);
      if (autoNext && i >= 0 && i < queue.length - 1) load(queue[i + 1]);
      else { a.currentTime = 0; setTime(0); }
    };
    a.addEventListener("ended", ended);
    return () => a.removeEventListener("ended", ended);
  }, [audio, repeat, autoNext, queue, current, load, setTime]);
};

const useQueue = ({ audio, current, load, setAutoNext }) => {
  const [queue, setQueue] = useState([]);
  const play = useCallback((song, list) => {
    if (!song?.audio_path) return;
    if (list) setQueue(playable(list));
    if (current?.id !== song.id) return load(song);
    if (audio.current.paused) audio.current.play();
    else audio.current.pause();
  }, [audio, current, load]);
  const step = useCallback((dir) => {
    if (!current || !queue.length) return;
    const i = queue.findIndex((s) => s.id === current.id);
    const nxt = queue[(i + dir + queue.length) % queue.length];
    if (nxt) load(nxt);
  }, [current, queue, load]);
  const switchTo = useCallback((song, list) => {
    if (list) setQueue(playable(list));
    load(song, audio.current.currentTime);
  }, [audio, load]);
  const playAll = useCallback((list, shuffle = false) => {
    const q = playable(list);
    if (!q.length) return;
    const order = shuffle ? [...q].sort(() => Math.random() - 0.5) : q;
    setQueue(order);
    setAutoNext(true);
    load(order[0]);
  }, [load, setAutoNext]);
  const next = useCallback(() => step(1), [step]);
  const prev = useCallback(() => step(-1), [step]);
  return { queue, play, switchTo, playAll, next, prev };
};

export function PlayerProvider({ children }) {
  const { audio, current, setCurrent, load } = useAudioEngine();
  const { playing, time, setTime, duration } = useAudioState(audio);
  const [volume, setVolume] = useState(0.85);
  const [repeat, setRepeat] = useState(false);
  const [lyricsOpen, setLyricsOpen] = useState(false);
  const [autoNext, setAutoNext] = useStoredFlag("dainuok_autonext");
  const { queue, play, switchTo, playAll, next, prev } = useQueue({ audio, current, load, setAutoNext });
  useEndedHandler(audio, { repeat, autoNext, queue, current, load, setTime });
  useEffect(() => { audio.current.volume = volume; }, [audio, volume]);

  const seek = useCallback((t) => { audio.current.currentTime = t; setTime(t); }, [audio, setTime]);
  const toggle = useCallback(() => {
    if (!current) return;
    if (audio.current.paused) audio.current.play();
    else audio.current.pause();
  }, [audio, current]);
  const updateCurrent = useCallback((song) => setCurrent((c) => (c?.id === song.id ? { ...c, ...song } : c)), [setCurrent]);

  const state = useMemo(() => ({ current, playing, time, duration, volume, repeat, autoNext, lyricsOpen }),
    [current, playing, time, duration, volume, repeat, autoNext, lyricsOpen]);
  const navigation = useMemo(() => ({ play, switchTo, playAll, next, prev }), [play, switchTo, playAll, next, prev]);
  const actions = useMemo(() => ({ ...navigation, toggle, seek, updateCurrent, setVolume, setRepeat, setAutoNext, setLyricsOpen }),
    [navigation, toggle, seek, updateCurrent, setAutoNext]);

  return (
    <PlayerActionsCtx.Provider value={actions}>
      <PlayerStateCtx.Provider value={state}>{children}</PlayerStateCtx.Provider>
    </PlayerActionsCtx.Provider>
  );
}
